/**
 * WmMultiPicker — 담당 WM 여러 명 고르기(공동관리 · 2026-10-26 지도사 「한 업체에 WM 여러 명 등록 가능하게 · 공동관리하는 개념이 있어」)
 *
 *   - 고른 분은 위에 한 줄씩(이름 · 소속 · 빼기). 첫 분이 대표 WM(보고서 받는 분) — 다른 분 줄의 「대표로」로 바꾼다
 *   - 아래 찾기 칸(PersonPicker 한 벌)으로 더한다 — 이름 · 소속 · 연락처 · 초성 · 없으면 그 자리에서 등록
 *   - noneValue 를 주면 아무도 안 골랐을 때 「없음」을 고를 수 있다(동행상담 등록은 WM 칸이 필수라 「없음」을 따로 고른다)
 */
import React, { useState } from 'react';
import type { FP, WM } from '../types';
import { C, T, R } from './ui';
import PersonPicker from './PersonPicker';

interface Props {
  people: WM[];
  value: string[];                          // 고른 WM id(대표 먼저)
  onChange: (ids: string[]) => void;
  none?: boolean;                           // 「없음」을 골랐나
  onNone?: (on: boolean) => void;           // 주면 「없음」 줄을 보인다
  noneLabel?: string;
  onCreated?: (p: FP | WM) => void;
  onToast?: (msg: string, type?: 'success' | 'error') => void;
  size?: 'md' | 'sm';
  max?: number;
}

const NONE = '__none__';

export default function WmMultiPicker({ people, value, onChange, none, onNone, noneLabel = '없음 — WM 없이 진행', onCreated, onToast, size = 'md', max = 10 }: Props) {
  const [made, setMade] = useState<WM[]>([]);   // 이 칸에서 막 등록한 분(위 목록이 다시 읽히기 전에도 이름이 보이게)
  const byId = new Map(made.concat(people).map((p) => [p.id, p]));
  const sm = size === 'sm';
  const rowH = sm ? 30 : 36;
  const fs = sm ? T.xs : T.sm;
  const lineBtn: React.CSSProperties = { height: sm ? 24 : 26, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.xs, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 };

  if (none && onNone) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: rowH, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.bg }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: fs, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{noneLabel}</span>
      <button type="button" onClick={() => onNone(false)} style={lineBtn}>WM 고르기</button>
    </div>
  );

  const remove = (id: string) => onChange(value.filter((x) => x !== id));
  const lead = (id: string) => onChange([id].concat(value.filter((x) => x !== id)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value.length > 0 && (
        <ul aria-label="고른 WM" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {value.map((id, i) => {
            const p = byId.get(id);
            return (
              <li key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: rowH, padding: '0 6px 0 10px', borderRadius: R.sm, border: `1px solid ${i === 0 ? C.accent : C.border}`, background: i === 0 ? C.accentLt : '#fff' }}>
                {/* 대표 · 함께 — 같은 자리 같은 폭 */}
                <span style={{ width: 34, flexShrink: 0, fontSize: 11, fontWeight: 700, color: i === 0 ? C.accent : C.sub }}>{i === 0 ? '대표' : '함께'}</span>
                <span style={{ fontSize: fs, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{p ? p.name : '등록 정보 없음'}</span>
                <span title={p?.company || ''} style={{ flex: 1, minWidth: 0, fontSize: T.xs, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.company || ''}</span>
                {i > 0 && <button type="button" onClick={() => lead(id)} style={lineBtn} title="이분을 대표 WM 으로(보고서를 받는 분)">대표로</button>}
                <button type="button" onClick={() => remove(id)} aria-label={`${p ? p.name : 'WM'} 빼기`} style={lineBtn}>빼기</button>
              </li>
            );
          })}
        </ul>
      )}
      {value.length < max && (
        <PersonPicker kind="wm" size={size} people={people} value="" exclude={value} clearAfterPick
          placeholder={value.length ? '함께 맡은 WM 더하기 (이름 · 소속 · 연락처)' : undefined}
          noneValue={onNone && value.length === 0 ? NONE : undefined} noneLabel={noneLabel}
          onChange={(id) => { if (!id) return; if (id === NONE) { onNone?.(true); return; } if (!value.includes(id)) onChange(value.concat([id])); }}
          onCreated={(p) => { setMade((m) => m.concat([p as WM])); onCreated?.(p); }} onToast={onToast} />
      )}
    </div>
  );
}
